import React, { Component } from "react";
import { Table, Grid } from "semantic-ui-react";
import find from "lodash/find";

import CardShell from "./CardShell";
import {
  resolveSubjectTemplate,
  isSuppressedRow,
  flattenTemplate,
  categoryMatches,
} from "../subjectTemplate";
import { clubRows, clubMark } from "../clubMembership";

const _ = { find };

/**
 * The descriptive report card, for Kindergarten and Grade 1 from SY 2026-2027
 * and for Grades 2 and 3 as DO 015 phases them in.
 *
 * Ratings are letters, so there is no arithmetic here at all: no final average
 * per subject, and deliberately no AVERAGE row and no "Promoted" label. DO 015
 * confers no academic honors at Key Stage 1 and reports progress through
 * descriptors and narrative feedback instead, so an average would be both
 * meaningless and against the order.
 *
 * The conduct row stays numeric, because ClassConduct is a separate model on
 * its own 1-15 scale and was not part of the descriptive change.
 */
class CardDescriptive extends Component {
  ratingFor(sectionSubject, period) {
    if (!sectionSubject) return "";
    const grade = _.find(
      this.props.summary.grades,
      (g) => g.sectionSubjectId === sectionSubject.sectionSubjectId
    );
    if (!grade) return "";
    return grade[period.letterField] || "";
  }

  render() {
    const {
      scheme,
      sectionStudent,
      sectionSubjects,
      teachers,
      remarks,
    } = this.props;

    const section = sectionStudent.section;
    const periods = scheme.periods;

    const template = resolveSubjectTemplate(section, scheme);
    const subjectCategories = flattenTemplate(template);

    const rows = subjectCategories.map((subjectCategory, key) => {
      const value = subjectCategory.value;
      if (isSuppressedRow(section.gradeLevel, value)) return null;

      const sectionSubject = _.find(
        sectionSubjects,
        (o) => categoryMatches(value, o.subject.subjectCategory)
      );

      return (
        <Table.Row key={key} className="students-table-row">
          <Table.Cell>{subjectCategory.text}</Table.Cell>
          {periods.map((p) => (
            <Table.Cell key={p.index} textAlign="center">
              <strong>{this.ratingFor(sectionSubject, p)}</strong>
            </Table.Cell>
          ))}
        </Table.Row>
      );
    });

    const classConduct =
      sectionStudent.classConducts && sectionStudent.classConducts.length !== 0
        ? sectionStudent.classConducts[0]
        : null;

    const adviser = _.find(teachers, (t) => t.teacherId === section.teacherId);

    return (
      <CardShell {...this.props} adviser={adviser}>
        <Grid.Row>
          <Grid.Column width={11}>
            <Table celled size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Learning Area
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    colSpan={String(periods.length)}
                    textAlign="center"
                  >
                    Level of Attainment
                  </Table.HeaderCell>
                </Table.Row>
                <Table.Row>
                  {periods.map((p, i) => (
                    <Table.HeaderCell
                      key={p.index}
                      collapsing
                      textAlign="center"
                      style={
                        i === 0
                          ? { borderLeft: "1px solid rgba(34,36,38,.1)" }
                          : undefined
                      }
                    >
                      {p.shortLabel}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {rows}

                {/* No AVERAGE row: Key Stage 1 has no general average. */}
                <Table.Row className="students-table-row">
                  <Table.Cell>
                    <span>Conduct</span>
                  </Table.Cell>
                  {periods.map((p) => {
                    const v = classConduct ? classConduct[p.conductField] : "";
                    return (
                      <Table.Cell key={p.index} textAlign="center">
                        {!v ? "" : v}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>

                {/* Clubs print under Conduct. Their marks are read the way every
                    other row on this card is read - as letters - because the
                    scheme is the section's, not the subject's, and grade entry
                    writes a Kinder or Grade 1 club to the letter columns. */}
                {clubRows(this.props.clubStudents, this.props.schoolYear).map((club, i) => (
                  <Table.Row key={`club-${i}`} className="students-table-row">
                    <Table.Cell>
                      <span>{club.label}</span>
                    </Table.Cell>
                    {periods.map((p) => (
                      <Table.Cell key={p.index} textAlign="center">
                        <strong>{clubMark(club.membership, scheme, p)}</strong>
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            <Table celled size="small" compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell colSpan="2" textAlign="center">
                    Legend
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {scheme.scaleOptions.map((option) => (
                  <Table.Row key={option.value}>
                    <Table.Cell collapsing textAlign="center">
                      <strong>{option.value}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {option.text.replace(option.value + " - ", "")}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Grid.Column>

          <Grid.Column width={5}>
            <Table celled size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell textAlign="center">Term</Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">Remarks</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {periods.map((p) => (
                  <Table.Row key={p.index} className="students-table-row">
                    <Table.Cell collapsing>{p.shortLabel}</Table.Cell>
                    <Table.Cell>
                      {remarks && remarks.length !== 0
                        ? remarks[0][p.remarkField]
                        : ""}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Grid.Column>
        </Grid.Row>
      </CardShell>
    );
  }
}

export default CardDescriptive;
